import { useState, useRef } from 'react'
import * as api from '../api'
import { useJobs } from '../jobs'
import { useIsMobile } from '../useMediaQuery'

/* ── Inline markdown renderer (same pattern as ChatPanel) ──────────────────── */
function inlineFormat (text) {
  return text
    .replace(
      /\*\*(.+?)\*\*/g,
      '<strong style="color:var(--ink);font-weight:600">$1</strong>'
    )
    .replace(/\*(.+?)\*/g, '<em style="color:var(--ink-3)">$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
}

function renderMarkdown (text) {
  if (!text) return null
  const lines = text.split('\n')
  const elements = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    if (line.startsWith('## ')) {
      elements.push(
        <h3
          key={i}
          style={{
            fontWeight: 700,
            fontSize: 14,
            margin: '12px 0 4px',
            color: 'var(--ink)'
          }}
        >
          {line.slice(3)}
        </h3>
      )
    } else if (line.startsWith('# ')) {
      elements.push(
        <h2
          key={i}
          style={{
            fontWeight: 700,
            fontSize: 16,
            margin: '14px 0 6px',
            color: 'var(--ink)'
          }}
        >
          {line.slice(2)}
        </h2>
      )
    } else if (line.startsWith('### ')) {
      elements.push(
        <h4
          key={i}
          style={{
            fontWeight: 600,
            fontSize: 13,
            margin: '8px 0 3px',
            color: 'var(--ink-2)'
          }}
        >
          {line.slice(4)}
        </h4>
      )
    } else if (
      line.startsWith('- ') ||
      line.startsWith('• ') ||
      line.startsWith('* ')
    ) {
      elements.push(
        <div
          key={i}
          style={{
            display: 'flex',
            gap: 8,
            padding: '2px 0',
            alignItems: 'flex-start'
          }}
        >
          <span
            style={{
              color: 'var(--accent-2)',
              flexShrink: 0,
              marginTop: 1,
              fontSize: 15
            }}
          >
            ·
          </span>
          <span
            style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--ink-2)' }}
            dangerouslySetInnerHTML={{ __html: inlineFormat(line.slice(2)) }}
          />
        </div>
      )
    } else if (line.match(/^\d+\.\s/)) {
      const content = line.replace(/^\d+\.\s/, '')
      const num = line.match(/^(\d+)/)[1]
      elements.push(
        <div
          key={i}
          style={{
            display: 'flex',
            gap: 9,
            padding: '2px 0',
            alignItems: 'flex-start'
          }}
        >
          <span
            style={{
              color: 'white',
              background: 'var(--accent)',
              fontWeight: 700,
              flexShrink: 0,
              fontSize: 10,
              borderRadius: '50%',
              width: 17,
              height: 17,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 3
            }}
          >
            {num}
          </span>
          <span
            style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--ink-2)' }}
            dangerouslySetInnerHTML={{ __html: inlineFormat(content) }}
          />
        </div>
      )
    } else if (line.trim() === '---') {
      elements.push(
        <hr
          key={i}
          style={{
            border: 'none',
            borderTop: '1px solid var(--border)',
            margin: '8px 0'
          }}
        />
      )
    } else if (
      line.startsWith('📌') ||
      line.startsWith('💡') ||
      line.startsWith('⚡') ||
      line.startsWith('🔑')
    ) {
      elements.push(
        <div
          key={i}
          style={{
            background: 'var(--accent-soft)',
            borderLeft: '3px solid var(--accent)',
            borderRadius: '0 var(--radius) var(--radius) 0',
            padding: '8px 12px',
            margin: '6px 0',
            fontSize: 13,
            lineHeight: 1.65
          }}
          dangerouslySetInnerHTML={{ __html: inlineFormat(line) }}
        />
      )
    } else if (line.trim()) {
      elements.push(
        <p
          key={i}
          style={{
            fontSize: 13,
            lineHeight: 1.75,
            color: 'var(--ink-2)',
            margin: '2px 0'
          }}
          dangerouslySetInnerHTML={{ __html: inlineFormat(line) }}
        />
      )
    } else {
      elements.push(<div key={i} style={{ height: 4 }} />)
    }
    i++
  }
  return elements
}

/* ── PDF Export helper ─────────────────────────────────────────────────────── */
function exportNotesAsPDF (notes, title = 'Syllabus Notes') {
  const printWindow = window.open('', '_blank')
  if (!printWindow) return

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Georgia', serif;
      font-size: 13px;
      line-height: 1.75;
      color: #1a1a2e;
      padding: 48px 56px;
      max-width: 800px;
      margin: 0 auto;
    }
    .header {
      border-bottom: 3px solid #6366f1;
      padding-bottom: 16px;
      margin-bottom: 28px;
    }
    .header h1 {
      font-size: 22px;
      font-weight: 700;
      color: #1a1a2e;
      margin-bottom: 4px;
    }
    .header .meta {
      font-size: 11px;
      color: #6b7280;
      font-family: 'Courier New', monospace;
    }
    .notes-section {
      margin-bottom: 32px;
    }
    .section-title {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: #6366f1;
      margin-bottom: 12px;
      padding-bottom: 6px;
      border-bottom: 1px solid #e5e7eb;
    }
    .file-badge {
      display: inline-block;
      background: #f0f0ff;
      border: 1px solid #c7d2fe;
      border-radius: 6px;
      padding: 4px 10px;
      font-size: 11px;
      color: #4f46e5;
      margin-bottom: 14px;
      font-family: 'Courier New', monospace;
    }
    h1, h2, h3, h4 { color: #1a1a2e; margin: 10px 0 4px; }
    h2 { font-size: 15px; font-weight: 700; margin-top: 14px; }
    h3 { font-size: 13.5px; font-weight: 600; }
    h4 { font-size: 12.5px; font-weight: 600; color: #374151; }
    p { margin: 2px 0; color: #374151; }
    ul, ol { margin: 8px 0 10px; padding-left: 24px; }
    li { margin: 4px 0; padding-left: 3px; overflow-wrap: anywhere; }
    code {
      background: #f3f4f6;
      padding: 1px 5px;
      border-radius: 3px;
      font-family: 'Courier New', monospace;
      font-size: 11.5px;
    }
    .callout {
      background: #eef2ff;
      border-left: 3px solid #6366f1;
      padding: 8px 12px;
      margin: 6px 0;
      border-radius: 0 4px 4px 0;
    }
    hr { border: 0; border-top: 1px solid #e5e7eb; height: 0; margin: 18px 0; }
    strong { color: #111827; font-weight: 600; }
    em { color: #6b7280; }
    .footer {
      margin-top: 40px;
      padding-top: 14px;
      border-top: 1px solid #e5e7eb;
      font-size: 10px;
      color: #9ca3af;
      display: flex;
      justify-content: space-between;
    }
    @media print {
      body { padding: 32px 40px; }
      @page { margin: 20mm; }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>${title}</h1>
    <div class="meta">Generated by StudyMind AI · ${new Date().toLocaleDateString(
      'en-IN',
      { year: 'numeric', month: 'long', day: 'numeric' }
    )}</div>
  </div>
  ${notes
    .map(
      n => `
    <div class="notes-section">
      <div class="section-title">Notes from syllabus</div>
      <div class="file-badge">📄 ${n.filename}</div>
      <div>${markdownToHTML(n.content)}</div>
    </div>
  `
    )
    .join('<hr style="margin:28px 0;border-top:2px dashed #e5e7eb;">')}
  <div class="footer">
    <span>StudyMind AI — Syllabus Notes</span>
    <span>Page 1</span>
  </div>
</body>
</html>`

  printWindow.document.write(html)
  printWindow.document.close()
  printWindow.onload = () => {
    printWindow.focus()
    printWindow.print()
  }
}

function markdownToHTML (text) {
  if (!text) return ''
  return text
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h2>$1</h2>')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^#### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^##### (.+)$/gm, '<h5>$1</h5>')
    .replace(/^###### (.+)$/gm, '<h6>$1</h6>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/^\* (.+)$/gm, '<li>$1</li>')
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, m => `<ul>${m}</ul>`)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(/^---$/gm, '<hr>')
    .replace(/^(📌|💡|⚡|🔑)(.+)$/gm, '<div class="callout">$1$2</div>')
    .replace(/\n{2,}/g, '</p><p>')
    .replace(/^(?!<[hul]|<div|<hr)(.+)$/gm, '<p>$1</p>')
    .replace(/<p><\/p>/g, '')
}

/* ── SyllabusPanel component ───────────────────────────────────────────────── */
export default function SyllabusPanel () {
  const isMobile = useIsMobile()
  const fileRef = useRef(null)
  const [dragging, setDragging] = useState(false)
  const [files, setFiles] = useState([])
  const [pastedText, setPastedText] = useState('')
  const [currentFileIdx, setCurrentFileIdx] = useState(0)
  const [exporting, setExporting] = useState(false)

  /* Generation runs as a global background job so it survives navigation and
   * can be cancelled from the jobs tray. */
  const { startJob, getJob } = useJobs()
  const job = getJob('syllabus', 'syllabus')
  const loading = job?.status === 'running'
  const notes = job?.status === 'done' ? job.result || [] : [] // [{filename, content}]
  const error = job?.status === 'error' ? job.error : null

  const addFiles = incoming => {
    const arr = Array.from(incoming || [])
    if (!arr.length) return
    setFiles(prev => {
      const seen = new Set(prev.map(f => f.name + f.size))
      return [...prev, ...arr.filter(f => !seen.has(f.name + f.size))]
    })
  }

  const removeFile = idx => {
    setFiles(prev => prev.filter((_, i) => i !== idx))
  }

  const handleGenerate = () => {
    let filesToUpload = [...files]

    if (pastedText.trim()) {
      const virtualFile = new File([pastedText], 'Pasted Syllabus.txt', {
        type: 'text/plain'
      })
      filesToUpload.push(virtualFile)
    }

    if (!filesToUpload.length || loading) return
    setCurrentFileIdx(0)

    const count = filesToUpload.length
    startJob({
      kind: 'syllabus',
      scopeId: 'syllabus',
      label: `Syllabus notes · ${count} file${count > 1 ? 's' : ''}`,
      doneLabel: 'Syllabus notes',
      run: signal => api.generateSyllabusNotes(filesToUpload, signal)
    })
  }

  const handleExport = () => {
    if (!notes.length) return
    setExporting(true)
    setTimeout(() => {
      exportNotesAsPDF(
        notes,
        notes.length === 1 ? `${notes[0].filename} — Notes` : 'Syllabus Notes'
      )
      setExporting(false)
    }, 100)
  }

  const activeNote = notes[currentFileIdx] || notes[0]

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'var(--bg)'
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 20px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexShrink: 0
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 9,
            background: 'var(--purple-soft, rgba(168,85,247,0.12))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(168,85,247,0.25)'
          }}
        >
          <span
            className='material-symbols-rounded'
            style={{
              fontSize: 17,
              color: 'var(--purple, #a855f7)',
              fontVariationSettings: "'FILL' 1"
            }}
          >
            import_contacts
          </span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 13.5 }}>Syllabus Notes</div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            Upload your syllabus — AI generates structured study notes
          </div>
        </div>

        {notes.length > 0 && (
          <button
            onClick={handleExport}
            disabled={exporting}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 13px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--green-soft, rgba(34,197,94,0.12))',
              border: '1px solid rgba(34,197,94,0.3)',
              color: 'var(--green, #22c55e)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all var(--transition)'
            }}
          >
            {exporting ? (
              <div
                className='spinner spinner-sm'
                style={{
                  borderTopColor: 'var(--green)',
                  borderColor: 'rgba(34,197,94,0.2)'
                }}
              />
            ) : (
              <span
                className='material-symbols-rounded'
                style={{ fontSize: 15, fontVariationSettings: "'FILL' 1" }}
              >
                picture_as_pdf
              </span>
            )}
            Export PDF
          </button>
        )}
      </div>

      {/* Body */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          overflow: 'hidden',
          minWidth: 0
        }}
      >
        {/* Left: Upload zone */}
        <div
          style={{
            width: isMobile ? '100%' : 260,
            borderRight: isMobile ? 'none' : '1px solid var(--border)',
            borderBottom: isMobile ? '1px solid var(--border)' : 'none',
            maxHeight: isMobile ? '42vh' : 'none',
            background: 'var(--surface)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            flexShrink: 0,
            padding: '14px 12px',
            gap: 12
          }}
        >
          {/* Drop zone */}
          <div
            onDragOver={e => {
              e.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={e => {
              e.preventDefault()
              setDragging(false)
              addFiles(e.dataTransfer.files)
            }}
            onClick={() => fileRef.current?.click()}
            style={{
              border: `2px dashed ${
                dragging
                  ? 'var(--accent)'
                  : files.length
                  ? 'rgba(168,85,247,0.4)'
                  : 'var(--border-2)'
              }`,
              borderRadius: 'var(--radius-xl)',
              padding: '20px 14px',
              textAlign: 'center',
              cursor: 'pointer',
              background: dragging
                ? 'var(--accent-soft)'
                : files.length
                ? 'var(--purple-soft, rgba(168,85,247,0.07))'
                : 'var(--surface-2)',
              transition: 'all var(--transition)'
            }}
          >
            <input
              ref={fileRef}
              type='file'
              multiple
              accept='.pdf,.txt,.md,.docx'
              style={{ display: 'none' }}
              onChange={e => {
                addFiles(e.target.files)
                e.target.value = ''
              }}
            />
            <span
              className='material-symbols-rounded'
              style={{
                fontSize: 28,
                color: files.length ? 'var(--purple, #a855f7)' : 'var(--ink-3)',
                fontVariationSettings: "'FILL' 1",
                display: 'block',
                marginBottom: 8
              }}
            >
              {files.length ? 'library_books' : 'upload_file'}
            </span>
            <div
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: files.length ? 'var(--purple, #a855f7)' : 'var(--ink-2)',
                marginBottom: 3
              }}
            >
              {files.length
                ? `${files.length} file${files.length > 1 ? 's' : ''} ready`
                : 'Drop syllabus files'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              PDF, TXT, MD · multiple files ok
            </div>
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 5
              }}
            >
              {files.map((f, i) => (
                <div
                  key={f.name + f.size}
                  onClick={() => notes.length && setCurrentFileIdx(i)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    background:
                      notes.length && currentFileIdx === i
                        ? 'var(--accent-soft)'
                        : 'var(--surface-2)',
                    border: '1px solid',
                    borderColor:
                      notes.length && currentFileIdx === i
                        ? 'rgba(99,102,241,0.3)'
                        : 'var(--border)',
                    borderRadius: 'var(--radius-md)',
                    padding: '8px 10px',
                    cursor: notes.length ? 'pointer' : 'default',
                    transition: 'all var(--transition)'
                  }}
                >
                  <span
                    className='material-symbols-rounded'
                    style={{
                      fontSize: 16,
                      color: f.name.endsWith('.pdf')
                        ? 'var(--rose, #f43f5e)'
                        : 'var(--sky, #0ea5e9)',
                      fontVariationSettings: "'FILL' 1",
                      flexShrink: 0
                    }}
                  >
                    {f.name.endsWith('.pdf') ? 'picture_as_pdf' : 'description'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 11.5,
                        fontWeight: 500,
                        color: 'var(--ink-2)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {f.name}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>
                      {(f.size / 1024).toFixed(0)} KB
                    </div>
                  </div>
                  <button
                    onClick={e => {
                      e.stopPropagation()
                      removeFile(i)
                    }}
                    className='btn btn-ghost btn-icon-sm'
                    disabled={loading}
                  >
                    <span
                      className='material-symbols-rounded'
                      style={{ fontSize: 14 }}
                    >
                      close
                    </span>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Divider OR */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 10,
              color: 'var(--ink-3)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              margin: '6px 0',
              flexShrink: 0
            }}
          >
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            <span>OR</span>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          </div>

          {/* Text area */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 5,
              flexShrink: 0,
              marginBottom: 4
            }}
          >
            <label
              style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink-3)' }}
            >
              Paste Syllabus Text
            </label>
            <textarea
              value={pastedText}
              onChange={e => setPastedText(e.target.value)}
              placeholder='Paste course outline, list of topics, or syllabus contents here...'
              style={{
                background: 'var(--surface-2)',
                border: '1px solid var(--border-2)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--ink)',
                fontFamily: 'var(--font-sans)',
                fontSize: 12,
                padding: '8px 10px',
                minHeight: 90,
                maxHeight: 150,
                resize: 'vertical',
                outline: 'none',
                transition: 'border-color var(--transition)'
              }}
              onFocus={e =>
                (e.target.style.borderColor = 'var(--border-focus)')
              }
              onBlur={e => (e.target.style.borderColor = 'var(--border-2)')}
            />
          </div>

          {/* Generate button */}
          <button
            onClick={handleGenerate}
            disabled={(!files.length && !pastedText.trim()) || loading}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: '11px 16px',
              borderRadius: 'var(--radius-lg)',
              background:
                (files.length || pastedText.trim()) && !loading
                  ? 'linear-gradient(135deg, var(--purple, #a855f7), var(--accent))'
                  : 'var(--surface-3)',
              border: 'none',
              color:
                (files.length || pastedText.trim()) && !loading
                  ? 'white'
                  : 'var(--ink-3)',
              fontSize: 13,
              fontWeight: 600,
              cursor:
                (files.length || pastedText.trim()) && !loading
                  ? 'pointer'
                  : 'default',
              transition: 'all var(--transition)',
              boxShadow:
                (files.length || pastedText.trim()) && !loading
                  ? '0 4px 16px rgba(168,85,247,0.3)'
                  : 'none',
              opacity: !(files.length || pastedText.trim()) ? 0.5 : 1,
              flexShrink: 0
            }}
          >
            {loading ? (
              <>
                <div
                  className='spinner spinner-sm'
                  style={{
                    borderTopColor: 'rgba(255,255,255,0.8)',
                    borderColor: 'rgba(255,255,255,0.2)'
                  }}
                />
                {files.length > 0
                  ? `Generating ${currentFileIdx + 1}/${files.length}…`
                  : 'Generating Notes…'}
              </>
            ) : (
              <>
                <span
                  className='material-symbols-rounded'
                  style={{ fontSize: 17, fontVariationSettings: "'FILL' 1" }}
                >
                  auto_awesome
                </span>
                Generate Notes
              </>
            )}
          </button>

          {error && (
            <div
              style={{
                padding: '8px 10px',
                background: 'var(--rose-soft, rgba(244,63,94,0.1))',
                border: '1px solid rgba(244,63,94,0.25)',
                borderRadius: 'var(--radius-md)',
                fontSize: 11.5,
                color: 'var(--rose, #f43f5e)'
              }}
            >
              {error}
            </div>
          )}
        </div>

        {/* Right: Notes output */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {notes.length === 0 && !loading ? (
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 20,
                padding: 40,
                textAlign: 'center'
              }}
            >
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  background: 'var(--purple-soft, rgba(168,85,247,0.1))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid rgba(168,85,247,0.2)'
                }}
              >
                <span
                  className='material-symbols-rounded'
                  style={{
                    fontSize: 34,
                    color: 'var(--purple, #a855f7)',
                    fontVariationSettings: "'FILL' 1"
                  }}
                >
                  import_contacts
                </span>
              </div>
              <div>
                <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>
                  Upload your syllabus
                </h2>
                <p
                  style={{
                    color: 'var(--ink-3)',
                    fontSize: 13,
                    lineHeight: 1.6,
                    maxWidth: 320
                  }}
                >
                  Drop one or more syllabus files on the left. The AI will
                  generate comprehensive, exam-ready notes organized by topic.
                </p>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  justifyContent: 'center',
                  maxWidth: 380
                }}
              >
                {[
                  '📋 Topic summaries',
                  '💡 Key concepts',
                  '📝 Exam tips',
                  '🔑 Important terms'
                ].map(chip => (
                  <span
                    key={chip}
                    style={{
                      padding: '6px 12px',
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-full)',
                      fontSize: 12,
                      color: 'var(--ink-2)'
                    }}
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>
          ) : loading ? (
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 16
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background:
                    'linear-gradient(135deg, var(--purple, #a855f7), var(--accent))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 20px rgba(168,85,247,0.35)'
                }}
              >
                <span
                  className='material-symbols-rounded'
                  style={{
                    fontSize: 28,
                    color: 'white',
                    fontVariationSettings: "'FILL' 1"
                  }}
                >
                  auto_awesome
                </span>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>
                  Generating notes…
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                  Processing {files[currentFileIdx]?.name} ({currentFileIdx + 1}{' '}
                  of {files.length})
                </div>
              </div>
              <div
                className='ai-indicator animate-fadeIn'
                style={{ justifyContent: 'center' }}
              >
                <span
                  className='material-symbols-rounded'
                  style={{ fontSize: 15, fontVariationSettings: "'FILL' 1" }}
                >
                  smart_toy
                </span>
                Reading syllabus
                <div className='dots'>
                  <div className='dot' />
                  <div className='dot' />
                  <div className='dot' />
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Notes file tabs when multiple */}
              {notes.length > 1 && (
                <div
                  style={{
                    display: 'flex',
                    gap: 0,
                    borderBottom: '1px solid var(--border)',
                    background: 'var(--surface)',
                    flexShrink: 0,
                    overflowX: 'auto'
                  }}
                >
                  {notes.map((n, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentFileIdx(i)}
                      style={{
                        padding: '9px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 11.5,
                        fontWeight: 500,
                        color:
                          currentFileIdx === i
                            ? 'var(--accent-2)'
                            : 'var(--ink-3)',
                        background: 'none',
                        border: 'none',
                        borderBottom: '2px solid',
                        borderBottomColor:
                          currentFileIdx === i
                            ? 'var(--accent)'
                            : 'transparent',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'all var(--transition)'
                      }}
                    >
                      <span
                        className='material-symbols-rounded'
                        style={{
                          fontSize: 14,
                          color: n.filename.endsWith('.pdf')
                            ? 'var(--rose)'
                            : 'var(--sky)',
                          fontVariationSettings: "'FILL' 1"
                        }}
                      >
                        {n.filename.endsWith('.pdf')
                          ? 'picture_as_pdf'
                          : 'description'}
                      </span>
                      {n.filename.length > 20
                        ? n.filename.slice(0, 18) + '…'
                        : n.filename}
                    </button>
                  ))}
                </div>
              )}

              {/* Notes content */}
              {activeNote && (
                <div
                  style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}
                >
                  {/* Notes header */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      marginBottom: 18,
                      padding: '10px 14px',
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-lg)'
                    }}
                  >
                    <div
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: 8,
                        background: 'var(--purple-soft, rgba(168,85,247,0.12))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <span
                        className='material-symbols-rounded'
                        style={{
                          fontSize: 16,
                          color: 'var(--purple, #a855f7)',
                          fontVariationSettings: "'FILL' 1"
                        }}
                      >
                        auto_awesome
                      </span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: 12.5,
                          fontWeight: 600,
                          color: 'var(--ink)'
                        }}
                      >
                        AI Notes — {activeNote.filename}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--ink-3)',
                          marginTop: 1
                        }}
                      >
                        Generated by StudyMind AI
                      </div>
                    </div>
                    <button
                      onClick={handleExport}
                      disabled={exporting}
                      title='Export as PDF'
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: '5px 10px',
                        borderRadius: 'var(--radius-md)',
                        background: 'var(--green-soft, rgba(34,197,94,0.1))',
                        border: '1px solid rgba(34,197,94,0.25)',
                        color: 'var(--green, #22c55e)',
                        fontSize: 11.5,
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all var(--transition)'
                      }}
                    >
                      <span
                        className='material-symbols-rounded'
                        style={{
                          fontSize: 14,
                          fontVariationSettings: "'FILL' 1"
                        }}
                      >
                        picture_as_pdf
                      </span>
                      Export PDF
                    </button>
                  </div>

                  {/* The notes */}
                  <div
                    className='md-content'
                    style={{
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-lg)',
                      padding: '16px 18px',
                      lineHeight: 1.75
                    }}
                  >
                    {renderMarkdown(activeNote.content)}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
